import { useState } from 'react';
import { Alert, Button, Container, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconCircleCheck, IconUserPlus } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { AccessRequest } from '~api';
import { joinPaths } from '~lib';
import { getAppEnv } from '~portal/env';

export const Route = createFileRoute('/request-access')({
  component: RequestAccessPage,
});

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'submitted' }
  | { status: 'error'; message: string };

function RequestAccessPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [state, setState] = useState<FormState>({ status: 'idle' });

  const submitting = state.status === 'submitting';

  const handleSubmit = async () => {
    const parsed = AccessRequest.submissionSchema.safeParse({
      email,
      name,
      company: company.trim() || undefined,
    });

    if (!parsed.success) {
      const [issue] = parsed.error.issues;
      setState({
        status: 'error',
        message: issue ? `${issue.path.join('.')}: ${issue.message}` : 'Please check your details',
      });
      return;
    }

    setState({ status: 'submitting' });

    try {
      const env = getAppEnv();
      const res = await fetch(joinPaths(env.backendApiUrl, '/v1/access-requests'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });

      if (res.status === 429) {
        setState({
          status: 'error',
          message: 'Too many requests from this network. Please try again later.',
        });
        return;
      }

      if (!res.ok) {
        setState({
          status: 'error',
          message: 'We could not submit your request. Please try again.',
        });
        return;
      }

      setState({ status: 'submitted' });
    } catch {
      setState({
        status: 'error',
        message: 'Unable to reach the server. Please check your connection and try again.',
      });
    }
  };

  return (
    <Container size="sm" py="xl" mih="100vh">
      <Stack align="center" justify="center" mih="80vh" gap="lg">
        <Paper withBorder p="xl" w="100%">
          {state.status === 'submitted' ? (
            <Stack gap="lg" align="center">
              <IconCircleCheck size={48} stroke={1.5} />
              <Title order={2} ta="center">
                Request received
              </Title>
              <Text ta="center" c="dimmed">
                Thanks — the H2 team reviews every request. If your request is approved you'll
                receive an email invitation to set up your account.
              </Text>
            </Stack>
          ) : (
            <Stack gap="lg">
              <Stack gap="xs" align="center">
                <IconUserPlus size={48} stroke={1.5} />
                <Title order={2} ta="center">
                  Request access
                </Title>
                <Text ta="center" c="dimmed" size="sm">
                  H2GC is invitation-only while in beta. Tell us who you are and we'll be in touch.
                </Text>
              </Stack>

              <TextInput
                label="Work email"
                placeholder="you@company.com"
                type="email"
                required
                value={email}
                maxLength={AccessRequest.MAX_EMAIL_LENGTH}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={submitting}
              />

              <TextInput
                label="Full name"
                placeholder="Jane Doe"
                required
                value={name}
                maxLength={AccessRequest.MAX_NAME_LENGTH}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={submitting}
              />

              <TextInput
                label="Company"
                description="Optional"
                placeholder="Acme Corp"
                value={company}
                maxLength={AccessRequest.MAX_COMPANY_LENGTH}
                onChange={(e) => setCompany(e.currentTarget.value)}
                disabled={submitting}
              />

              {state.status === 'error' && (
                <Alert color="red" title="Unable to submit">
                  {state.message}
                </Alert>
              )}

              <Button onClick={handleSubmit} loading={submitting} fullWidth>
                Request access
              </Button>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
